/**
 * Jewelry Prompt Intermediary
 *
 * Converts free-form customer text into a structured jewelry design spec (JSON)
 * and produces a high-quality, consistent image-generation prompt.
 *
 * Exposes window.JewelryPromptIntermediary with:
 *   - buildSpec(rawText) -> structured spec object
 *   - buildPrompt(spec)  -> final precision prompt string
 *   - process(rawText)   -> { spec, prompt }
 *
 * Designed for Saif Jewelers modern / hip-hop / high-end aesthetic.
 */
(function () {
  var KNOWLEDGE = {
    categories: {
      ring: ["ring", "band", "solitaire"],
      necklace: ["necklace", "chain", "rope", "tennis chain", "cuban", "cuban link", "figaro", "franco"],
      pendant: ["pendant", "charm", "medallion", "piece"],
      bracelet: ["bracelet", "tennis bracelet", "bangle", "cuff"],
      earring: ["earring", "earrings", "studs", "hoops", "huggies", "drop earring"],
      anklet: ["anklet", "ankle chain"],
      brooch: ["brooch", "pin", "lapel"],
      grillz: ["grillz", "grill", "fronts"],
      watch: ["watch", "iced watch", "diamond watch"]
    },
    metals: {
      "yellow gold": ["yellow gold", "yg", "gold (yellow)", "14ky", "10ky", "18ky"],
      "white gold": ["white gold", "wg", "14kw", "10kw", "18kw"],
      "rose gold": ["rose gold", "pink gold", "rg", "14krg"],
      "two-tone gold": ["two tone", "two-tone", "dual tone", "mixed gold"],
      platinum: ["platinum", "plat", "950"],
      "sterling silver": ["sterling silver", "925", "silver"],
      tungsten: ["tungsten"],
      titanium: ["titanium"],
      "stainless steel": ["stainless", "stainless steel"]
    },
    karats: {
      "10k": ["10k", "10 karat", "10kt"],
      "14k": ["14k", "14 karat", "14kt"],
      "18k": ["18k", "18 karat", "18kt"],
      "24k": ["24k", "24 karat", "pure gold"]
    },
    finishes: {
      polished: ["polished", "high polish", "shiny", "mirror"],
      matte: ["matte", "satin"],
      brushed: ["brushed"],
      hammered: ["hammered"],
      antique: ["antique", "oxidized", "vintage finish"]
    },
    stoneTypes: {
      diamond: ["diamond", "diamonds", "vs", "vvs", "lab diamond", "moissanite"],
      sapphire: ["sapphire"],
      emerald: ["emerald"],
      ruby: ["ruby"],
      topaz: ["topaz"],
      opal: ["opal"],
      onyx: ["onyx"],
      pearl: ["pearl"],
      cz: ["cz", "cubic zirconia"]
    },
    stoneCuts: {
      round: ["round", "round brilliant"],
      oval: ["oval"],
      emerald: ["emerald cut", "emerald-cut"],
      princess: ["princess"],
      cushion: ["cushion"],
      pear: ["pear", "teardrop"],
      marquise: ["marquise"],
      heart: ["heart cut", "heart-shaped"],
      asscher: ["asscher"],
      radiant: ["radiant"],
      baguette: ["baguette"],
      "trapezoid": ["trapezoid"]
    },
    stoneSettings: {
      pave: ["pave", "pavé", "iced", "iced out", "iced-out", "fully iced"],
      prong: ["prong", "4 prong", "6 prong", "claw"],
      bezel: ["bezel"],
      channel: ["channel"],
      "tension": ["tension"],
      cluster: ["cluster", "halo cluster"],
      halo: ["halo"],
      "three stone": ["three stone", "trilogy"],
      "invisible": ["invisible", "invisible setting"]
    },
    aesthetics: {
      "hip-hop": ["hip hop", "hip-hop", "iced", "iced out", "iced-out", "bustdown", "blinged", "flooded", "stunner"],
      "luxury": ["luxury", "high-end", "high end", "premium", "couture", "bespoke", "fine jewelry"],
      "modern": ["modern", "minimal", "minimalist", "sleek", "clean", "contemporary", "geometric"],
      "vintage": ["vintage", "art deco", "antique", "victorian", "edwardian", "retro"],
      "bold": ["bold", "statement", "chunky", "oversized", "big"],
      "dainty": ["dainty", "delicate", "petite", "subtle", "small"],
      "edgy": ["edgy", "gothic", "skull", "spike", "punk"],
      "romantic": ["romantic", "love"]
    },
    motifs: [
      "heart", "cross", "star", "infinity", "lock", "key", "snake", "lyre",
      "anchor", "crown", "skull", "evil eye", "horseshoe", "compass",
      "feather", "flower", "rose", "butterfly", "lion", "eagle", "dragon",
      "dollar", "money", "praying hands", "last supper", "jesus piece",
      "virgin mary", "guardian angel"
    ],
    audiences: {
      men: ["men", "men's", "mens", "male", "him", "boyfriend", "husband", "father", "dad"],
      women: ["women", "women's", "womens", "female", "her", "girlfriend", "wife", "mother", "mom", "ladies"],
      unisex: ["unisex", "everyone", "anyone"]
    },
    occasions: {
      engagement: ["engagement", "propose", "proposal"],
      wedding: ["wedding", "bridal"],
      anniversary: ["anniversary"],
      everyday: ["everyday", "daily", "casual"],
      statement: ["statement", "show stopper", "showstopper", "going out"],
      gift: ["gift", "birthday", "valentine"]
    },
    chainStyles: ["cuban", "cuban link", "miami cuban", "rope", "figaro", "franco", "byzantine", "box", "snake", "wheat", "curb", "tennis"]
  };

  // Curated training library — examples of high-quality reference prompts
  // The system uses these to mirror tone and structure on similar inputs
  var TRAINING_EXAMPLES = [
    {
      tags: ["hip-hop", "pendant", "iced-out"],
      reference: "Miami-style iced-out hip hop pendant, fully encrusted with VVS round brilliant diamonds in pavé setting, polished white gold, crisp facet detail, hyper-detailed jewelry product photography"
    },
    {
      tags: ["luxury", "ring", "engagement"],
      occasion: "engagement",
      reference: "High-end engagement ring, oval cut diamond center stone with hidden halo, polished platinum band, micro-pavé side stones, fine jewelry editorial photography"
    },
    {
      tags: ["modern", "minimal", "ring"],
      reference: "Modern minimalist ring, clean architectural lines, brushed 18k yellow gold, single bezel-set diamond, contemporary fine jewelry product photography"
    },
    {
      tags: ["hip-hop", "necklace", "cuban"],
      reference: "Miami Cuban link chain, fully iced with VVS diamonds, polished white gold finish, thick gauge, premium hip hop jewelry studio photo"
    },
    {
      tags: ["luxury", "tennis bracelet"],
      reference: "Premium tennis bracelet, line of round brilliant diamonds in four-prong settings, polished 14k white gold, even spacing, luxury fine jewelry photography"
    },
    {
      tags: ["ring", "wedding", "men"],
      occasion: "wedding",
      audience: "men",
      reference: "Men's wedding band, sleek polished metal ring with comfort-fit interior, masculine architectural profile, fine jewelry product photography"
    }
  ];

  function tokenize(text) {
    return String(text || "")
      .toLowerCase()
      .replace(/[^a-z0-9\s\-']/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  function escapeRegex(str) {
    return String(str).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }

  // Word-boundary match so "men" doesn't match inside "women", "rose" doesn't
  // match inside "rose gold" as a motif, etc.
  function containsWord(haystack, needle) {
    var pattern = new RegExp("(^|[^a-z0-9])" + escapeRegex(needle) + "($|[^a-z0-9])", "i");
    return pattern.test(haystack);
  }

  function containsAny(haystack, needles) {
    for (var i = 0; i < needles.length; i += 1) {
      if (containsWord(haystack, needles[i])) {
        return true;
      }
    }
    return false;
  }

  function findFirstMatch(text, dictionary) {
    var keys = Object.keys(dictionary);
    for (var i = 0; i < keys.length; i += 1) {
      if (containsAny(text, dictionary[keys[i]])) {
        return keys[i];
      }
    }
    return null;
  }

  function findAllMatches(text, dictionary) {
    var matches = [];
    Object.keys(dictionary).forEach(function (key) {
      if (containsAny(text, dictionary[key])) {
        matches.push(key);
      }
    });
    return matches;
  }

  function findMotifs(text) {
    return KNOWLEDGE.motifs.filter(function (motif) {
      return containsWord(text, motif);
    });
  }

  function findChainStyle(text) {
    for (var i = 0; i < KNOWLEDGE.chainStyles.length; i += 1) {
      if (containsWord(text, KNOWLEDGE.chainStyles[i])) {
        return KNOWLEDGE.chainStyles[i];
      }
    }
    return null;
  }

  function applyAestheticHeuristics(spec, aesthetics) {
    // "iced out" implies pave diamonds + white/yellow gold default
    if (aesthetics.indexOf("hip-hop") > -1) {
      if (!spec.stones.length) {
        spec.stones.push({ type: "diamond", cut: "round", setting: "pave" });
      } else {
        spec.stones.forEach(function (stone) {
          if (!stone.setting) {
            stone.setting = "pave";
          }
        });
      }
      if (!spec.metal.type) {
        spec.metal.type = "white gold";
      }
      if (!spec.metal.finish) {
        spec.metal.finish = "polished";
      }
      if (!spec.metal.karat) {
        spec.metal.karat = "14k";
      }
    }

    // "luxury" implies fine cut quality + polished
    if (aesthetics.indexOf("luxury") > -1) {
      if (!spec.metal.finish) {
        spec.metal.finish = "polished";
      }
      if (!spec.metal.type) {
        spec.metal.type = "platinum";
      }
    }

    // "modern" implies clean finish, smaller stones, geometric
    if (aesthetics.indexOf("modern") > -1) {
      if (!spec.metal.finish) {
        spec.metal.finish = "brushed";
      }
    }

    return spec;
  }

  function selectTrainingReference(spec, aesthetics) {
    var bestMatch = null;
    var bestScore = 0;
    TRAINING_EXAMPLES.forEach(function (example) {
      // Reject examples whose occasion or audience contradicts the spec —
      // e.g. don't recommend the engagement-ring reference for a wedding band.
      if (example.occasion && spec.occasion && example.occasion !== spec.occasion) {
        return;
      }
      if (example.audience && spec.audience && example.audience !== spec.audience) {
        return;
      }
      var score = 0;
      example.tags.forEach(function (tag) {
        if (aesthetics.indexOf(tag) > -1) {
          score += 2;
        }
        if (spec.category && tag === spec.category) {
          score += 1;
        }
        if (spec.occasion && tag === spec.occasion) {
          score += 3;
        }
        if (spec.audience && tag === spec.audience) {
          score += 2;
        }
      });
      if (score > bestScore) {
        bestScore = score;
        bestMatch = example;
      }
    });
    // Require at least an aesthetic+category overlap (or stronger). A bare
    // category match (score 1) is not specific enough to anchor the prompt.
    return bestScore >= 3 ? bestMatch : null;
  }

  function buildSpec(rawText) {
    var text = tokenize(rawText);
    var spec = {
      raw_input: rawText,
      category: findFirstMatch(text, KNOWLEDGE.categories),
      metal: {
        type: findFirstMatch(text, KNOWLEDGE.metals),
        karat: findFirstMatch(text, KNOWLEDGE.karats),
        finish: findFirstMatch(text, KNOWLEDGE.finishes)
      },
      stones: [],
      aesthetics: findAllMatches(text, KNOWLEDGE.aesthetics),
      motifs: findMotifs(text),
      audience: findFirstMatch(text, KNOWLEDGE.audiences),
      occasion: findFirstMatch(text, KNOWLEDGE.occasions),
      chain_style: findChainStyle(text)
    };

    // Detect stones
    var stoneType = findFirstMatch(text, KNOWLEDGE.stoneTypes);
    var stoneCut = findFirstMatch(text, KNOWLEDGE.stoneCuts);
    var stoneSetting = findFirstMatch(text, KNOWLEDGE.stoneSettings);
    if (stoneType || stoneCut || stoneSetting) {
      spec.stones.push({
        type: stoneType || (spec.aesthetics.indexOf("hip-hop") > -1 ? "diamond" : null),
        cut: stoneCut,
        setting: stoneSetting
      });
    }

    // Apply heuristics based on aesthetic context
    applyAestheticHeuristics(spec, spec.aesthetics);

    // Reasonable defaults if nothing detected
    if (!spec.category) {
      // Try guessing from common phrases
      if (text.indexOf("chain") > -1) {
        spec.category = "necklace";
      }
    }

    // Determine budget tier from aesthetics
    if (spec.aesthetics.indexOf("luxury") > -1) {
      spec.budget_tier = "luxury";
    } else if (spec.aesthetics.indexOf("hip-hop") > -1) {
      spec.budget_tier = "premium";
    } else {
      spec.budget_tier = "mid";
    }

    return spec;
  }

  function describeStones(stones) {
    if (!stones.length) {
      return "";
    }
    return stones.map(function (stone) {
      var parts = [];
      if (stone.setting) {
        parts.push(stone.setting + " set");
      }
      if (stone.cut) {
        parts.push(stone.cut + " cut");
      }
      if (stone.type) {
        parts.push(stone.type + (stone.type === "diamond" ? "s" : ""));
      }
      return parts.join(" ");
    }).filter(Boolean).join(", ");
  }

  function describeMetal(metal) {
    var parts = [];
    if (metal.finish) {
      parts.push(metal.finish);
    }
    if (metal.karat) {
      parts.push(metal.karat);
    }
    if (metal.type) {
      parts.push(metal.type);
    }
    return parts.join(" ").trim();
  }

  function buildPrompt(spec) {
    var trainingRef = selectTrainingReference(spec, spec.aesthetics);

    var aestheticPhrase = spec.aesthetics.length
      ? spec.aesthetics.join(", ") + " style"
      : "modern fine jewelry";

    var audienceWord = "";
    if (spec.audience === "men") {
      audienceWord = "men's";
    } else if (spec.audience === "women") {
      audienceWord = "women's";
    } else if (spec.audience === "unisex") {
      audienceWord = "unisex";
    }

    // Category noun absorbs the occasion when it changes the noun itself
    // (e.g. "ring" + occasion "wedding" → "wedding band"). This prevents
    // wedding bands from being described as plain rings, which biases image
    // generation toward engagement-ring imagery.
    var categoryNoun = spec.category || "jewelry piece";
    var occasionConsumed = false;
    if (spec.category === "ring") {
      if (spec.occasion === "wedding") {
        categoryNoun = "wedding band";
        occasionConsumed = true;
      } else if (spec.occasion === "engagement") {
        categoryNoun = "engagement ring";
        occasionConsumed = true;
      } else if (spec.occasion === "anniversary") {
        categoryNoun = "anniversary ring";
        occasionConsumed = true;
      }
    }

    var coreSubject = [];
    if (aestheticPhrase) {
      coreSubject.push(aestheticPhrase);
    }
    if (audienceWord) {
      coreSubject.push(audienceWord);
    }
    if (spec.occasion && !occasionConsumed && spec.occasion !== "everyday") {
      coreSubject.push(spec.occasion);
    }
    coreSubject.push(categoryNoun);

    var subjectLine = coreSubject.join(" ");

    var detailParts = [];
    var metalPhrase = describeMetal(spec.metal);
    if (metalPhrase) {
      detailParts.push(metalPhrase);
    }
    var stonePhrase = describeStones(spec.stones);
    if (stonePhrase) {
      detailParts.push(stonePhrase);
    }
    if (spec.chain_style) {
      detailParts.push(spec.chain_style + " chain style");
    }
    if (spec.motifs.length) {
      detailParts.push(spec.motifs.join(" + ") + " motif");
    }

    var detailLine = detailParts.length ? detailParts.join(", ") : "";

    var renderingDirectives = [
      "ultra-detailed jewelry product photography",
      "isolated piece on plain pure white background (#ffffff)",
      "no scene, no props, no hands, no model, no shadow scene",
      "studio softbox lighting, soft minimal shadows",
      "centered composition, 3/4 angle, sharp focus",
      "macro detail on faceting and metalwork",
      "ecommerce catalog style, photorealistic, clean and crisp"
    ];

    var negatives = [
      "no people",
      "no hands holding",
      "no jewelry box",
      "no fabric",
      "no environment",
      "no text",
      "no watermark",
      "no logo",
      "no jewelry tray",
      "no wood surface"
    ];

    var prompt = subjectLine;
    if (detailLine) {
      prompt += ", " + detailLine;
    }
    if (trainingRef) {
      prompt += ". Reference style: " + trainingRef.reference;
    }
    prompt += ". " + renderingDirectives.join(", ");
    prompt += ". Avoid: " + negatives.join(", ") + ".";

    return prompt;
  }

  function process(rawText) {
    var spec = buildSpec(rawText);
    var prompt = buildPrompt(spec);
    return { spec: spec, prompt: prompt };
  }

  window.JewelryPromptIntermediary = {
    buildSpec: buildSpec,
    buildPrompt: buildPrompt,
    process: process,
    KNOWLEDGE: KNOWLEDGE,
    TRAINING_EXAMPLES: TRAINING_EXAMPLES
  };
})();
